// Turn the newest authoritative snapshot into a smooth picture.
//
// This never advances a body beyond a position the server sent. Each displayed
// point only catches up to the newest point, which removes the visible 20 Hz
// stair-step without prediction, reconciliation or another simulation.

import { TILE } from "../../sim/constants";
import type { EnemyView, PlayerView, Snapshot } from "../../shared/protocol";

interface Position {
  x: number;
  y: number;
}

type BodyView = PlayerView | EnemyView;

/** About two frames at 60 fps. It rounds off a server step without making the
 *  body feel detached from the latest position. */
const SMOOTH_SECONDS = 0.035;
const SNAP_DISTANCE = TILE * 2;

export class SnapshotView {
  private positions = new Map<number, Position>();
  /** Slugs, which the world sends once when a body enters view. Everything
   *  downstream of `advance` is entitled to a slug on every body, so this is
   *  where the one that was sent earlier comes back. */
  private slugs = new Map<number, string>();
  private tick = -1;

  advance(snap: Snapshot, dt: number): Snapshot {
    if (snap.tick !== this.tick) {
      this.tick = snap.tick;
      const present = new Set<number>();
      for (const body of snap.players) present.add(body.id);
      for (const body of snap.enemies) present.add(body.id);
      for (const id of this.positions.keys()) {
        if (!present.has(id)) this.positions.delete(id);
      }
      // A body that leaves view is forgotten on both counts. The world resends
      // the slug when it comes back, so holding one for every body ever seen
      // would be a leak that buys nothing.
      for (const id of this.slugs.keys()) {
        if (!present.has(id)) this.slugs.delete(id);
      }
    }

    const amount = 1 - Math.exp(-Math.max(0, dt) / SMOOTH_SECONDS);
    // The local body is already delayed by the trip to the authority and back.
    // Draw its newest confirmed position immediately; easing it adds latency
    // without adding information. Remote bodies still benefit from smoothing.
    const players = snap.players.map((body) => {
      const named = this.named(body);
      return named.id === snap.selfId ? this.latest(named) : this.smooth(named, amount);
    });
    const enemies = snap.enemies.map((body) => this.smooth(body, amount));
    return { ...snap, players, enemies };
  }

  /** A body with its slug on it, whether or not this frame carried one. */
  private named(body: PlayerView): PlayerView {
    if (body.slug !== undefined) {
      this.slugs.set(body.id, body.slug);
      return body;
    }
    const remembered = this.slugs.get(body.id);
    return remembered === undefined ? body : { ...body, slug: remembered };
  }

  private latest<T extends BodyView>(body: T): T {
    this.positions.set(body.id, { x: body.x, y: body.y });
    return body;
  }

  private smooth<T extends BodyView>(body: T, amount: number): T {
    let shown = this.positions.get(body.id);
    const dx = shown ? body.x - shown.x : 0;
    const dy = shown ? body.y - shown.y : 0;
    if (!shown || Math.abs(dx) > SNAP_DISTANCE || Math.abs(dy) > SNAP_DISTANCE) {
      shown = { x: body.x, y: body.y };
      this.positions.set(body.id, shown);
    } else {
      // A delayed display can still be finishing one lane when the authority
      // has turned onto the next. Catch the perpendicular axis up to the turn
      // point, then ease only along the new heading. This keeps the picture on
      // the same lanes as the simulation instead of cutting a diagonal corner.
      if (dx !== 0 && dy !== 0) {
        const moveDir = "moveDir" in body ? body.moveDir : body.dir;
        if (moveDir === 2 || moveDir === 3) {
          shown.y = body.y;
          shown.x += dx * amount;
        } else {
          shown.x = body.x;
          shown.y += dy * amount;
        }
      } else {
        shown.x += dx * amount;
        shown.y += dy * amount;
      }
      if (Math.abs(body.x - shown.x) < 0.01) shown.x = body.x;
      if (Math.abs(body.y - shown.y) < 0.01) shown.y = body.y;
    }
    if (shown.x === body.x && shown.y === body.y) return body;
    return { ...body, x: shown.x, y: shown.y };
  }
}
